import { payablePeriods } from "@chams-pay/application";
import type {
  AuthenticatedUserContract,
  LocalFinancialResultContract,
  Locale,
  OperationalPaymentContract,
  StudentLifecycleStatusContract,
  StudentProfileContract,
} from "@chams-pay/contracts";
import {
  normalizeSchoolLevelLabel,
  operationsMessages,
  paymentMessages,
  translate,
} from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { FinancialDocument } from "@chams-pay/document-ui";
import { Block, Segmented, SegmentedButton } from "konsta/react";
import { ChevronDown, Funnel, Plus, Search, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { initialOf, periodLabel } from "../core/format.js";
import { nextOpenScheduleYear, studentObligationYears } from "../core/student-obligation-years.js";
import { useStudentServiceAmounts } from "../core/use-operations.js";
import { usePayment } from "../core/use-payment.js";
import { useStudentLifecycle } from "../core/use-student-lifecycle.js";
import type { StudentSearchController } from "../core/use-students.js";
import type { WorkspaceController } from "../core/use-workspace.js";
import { deploymentLevelLabels } from "../core/deployment.js";
import { FilterSelect } from "../ui/filter-select.js";
import { Amount, Bidi, Count, DateValue, ServiceIcons } from "../ui/figures.js";
import { StudentListSummary } from "../ui/student-list-summary.js";
import { periodState, periodStateKey, StudentsEmptyState } from "../desktop/surfaces.js";
import type { PeriodState } from "../desktop/surfaces.js";
import { showMobileFeedback } from "./app-feedback.js";
import { MobileA4DocumentPreview } from "./a4-document-preview.js";
import { MobilePaymentPopup } from "./payment.js";
import { MobileOperationalPaymentPopup } from "./payment-detail.js";
import { TabbarSpacer } from "./shell.js";
import { MobileStudentLifecycle } from "./student-lifecycle.js";
import { MobileStudentFormPopup, MobileSurface } from "./surfaces.js";

const stateDot: Record<PeriodState, string> = {
  paid: "bg-success",
  overdue: "bg-destructive",
  due: "bg-warning",
  upcoming: "bg-border",
};

const stateText: Record<PeriodState, string> = {
  paid: "text-success",
  overdue: "text-destructive",
  due: "text-warning",
  upcoming: "text-muted-foreground",
};

const serviceAmountFields = [
  { family: "registration", label: "sp.enrollmentFee" },
  { family: "tuition", label: "sp.tuition" },
  { family: "transport", label: "sp.transport" },
  { family: "accompaniment", label: "sp.accompaniment" },
] as const;

const lifecycleStatusKey: Record<
  StudentLifecycleStatusContract,
  "sp.statusActive" | "sp.statusWithdrawn" | "sp.statusGraduated"
> = {
  active: "sp.statusActive",
  withdrawn: "sp.statusWithdrawn",
  graduated: "sp.statusGraduated",
};

function classOptionsOf(_workspace: WorkspaceController): readonly string[] {
  return deploymentLevelLabels;
}

export function MobileStudentsHeaderTools({
  locale,
  students,
  workspace,
}: {
  readonly locale: Locale;
  readonly students: StudentSearchController;
  readonly workspace: WorkspaceController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filtersOpen) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (filtersRef.current?.contains(target)) return;
      if (target.closest('[data-slot="select-content"]')) return;
      setFiltersOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [filtersOpen]);

  return (
    <div ref={filtersRef} className="mt-3.5 flex flex-col gap-2.5">
      <span className="font-heading text-2xs font-bold text-white/85">
        <StudentListSummary locale={locale} students={students} />
      </span>
      {filtersOpen && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t("common.close")}
          className="fixed inset-0 z-0 cursor-default bg-transparent"
          onClick={() => setFiltersOpen(false)}
        />
      )}
      <form
        className="student-search chams-field-shell relative z-10 flex h-[46px] items-center gap-2.5 rounded-2xl border border-transparent bg-white pe-0 ps-3.5 transition-colors focus-within:border-teal-200 focus-within:ring-3 focus-within:ring-white/25"
        role="search"
        dir={locale === "ar" ? "rtl" : "ltr"}
        aria-busy={students.busy}
        onSubmit={(event) => {
          event.preventDefault();
          students.searchNow(students.query);
        }}
      >
        {students.busy ? (
          <span
            aria-hidden="true"
            className="size-4 shrink-0 animate-spin rounded-full border-2 border-teal-100 border-t-primary"
          />
        ) : (
          <Search aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        )}
        <label className="flex-1">
          <span className="sr-only">{t("students.search")}</span>
          <input
            type="text"
            className="h-10 w-full bg-transparent text-start text-sm text-foreground outline-none focus-visible:outline-none"
            value={students.query}
            placeholder={t("sp.searchHint")}
            onChange={(event) => students.setQuery(event.target.value)}
          />
        </label>
        <span className="flex shrink-0 items-center gap-0.5">
          {students.query && (
            <button
              type="button"
              className="chams-focusable grid size-10 shrink-0 place-items-center rounded-xl text-muted-foreground"
              aria-label={t("sp.clearSearch")}
              onClick={() => students.setQuery("")}
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          )}
          <button
            type="button"
            className="relative grid size-10 shrink-0 place-items-center rounded-xl text-muted-foreground"
            aria-label={t("sp.filters")}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <Funnel aria-hidden="true" className="size-4" />
            {students.filtersActive && (
              <span
                aria-hidden="true"
                className="absolute end-1.5 top-1.5 size-2 rounded-full bg-warning"
              />
            )}
          </button>
        </span>
      </form>
      {filtersOpen && (
        <div className="relative z-10 flex flex-col gap-2.5 rounded-2xl bg-white p-3.5 text-foreground shadow-e2">
          <label className="flex flex-col gap-1 text-2xs font-semibold text-muted-foreground">
            {t("sp.class")}
            <FilterSelect
              value={students.filters.classLabel ?? "__all__"}
              className="h-11 rounded-xl bg-white"
              options={[
                { value: "__all__", label: t("sp.filterAll") },
                ...classOptionsOf(workspace).map((classLabel) => ({
                  value: classLabel,
                  label: classLabel,
                })),
              ]}
              onChange={(value) =>
                students.setFilters({
                  ...students.filters,
                  classLabel: value === "__all__" ? null : value,
                })
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-2xs font-semibold text-muted-foreground">
            {t("sp.lifecycleStatus")}
            <FilterSelect
              value={students.filters.lifecycleStatus}
              className="h-11 rounded-xl bg-white"
              options={[
                { value: "all", label: t("sp.filterAll") },
                { value: "active", label: t("sp.statusActive") },
                { value: "withdrawn", label: t("sp.statusWithdrawn") },
                { value: "graduated", label: t("sp.statusGraduated") },
              ]}
              onChange={(value) =>
                students.setFilters({
                  ...students.filters,
                  lifecycleStatus: value as typeof students.filters.lifecycleStatus,
                })
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-2xs font-semibold text-muted-foreground">
            {t("sp.paymentStatus")}
            <FilterSelect
              value={students.filters.paymentStatus}
              className="h-11 rounded-xl bg-white"
              options={[
                { value: "all", label: t("sp.filterAll") },
                { value: "up-to-date", label: t("sp.upToDate") },
                { value: "one-month-late", label: t("dash.oneMonth") },
                { value: "two-months-late", label: t("dash.twoMonths") },
                { value: "three-plus-months-late", label: t("dash.threePlus") },
              ]}
              onChange={(value) => {
                const paymentStatus = value as typeof students.filters.paymentStatus;
                students.setFilters({
                  ...students.filters,
                  paymentStatus,
                  arrears: null,
                  arrearsAsOf: null,
                  arrearsAcademicYear: null,
                });
              }}
            />
          </label>
          {students.filters.arrears && (
            <div className="flex min-h-10 items-center gap-2 rounded-xl bg-warning-soft px-3 text-xs font-semibold text-warning">
              <span className="min-w-0 flex-1 truncate">
                {t(
                  students.filters.arrears === "one"
                    ? "dash.oneMonth"
                    : students.filters.arrears === "two"
                      ? "dash.twoMonths"
                      : students.filters.arrears === "three-plus"
                        ? "dash.threePlus"
                        : students.filters.arrears === "urgent"
                          ? "dash.urgentArrears"
                          : students.filters.arrears === "registration"
                            ? "dash.registrationAttention"
                            : "sp.filterArrears",
                )}
              </span>
              <button
                type="button"
                className="grid size-8 place-items-center rounded-lg"
                aria-label={t("sp.clearFilters")}
                onClick={() =>
                  students.setFilters({
                    ...students.filters,
                    arrears: null,
                    arrearsAsOf: null,
                    arrearsAcademicYear: null,
                  })
                }
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </div>
          )}
          <button
            type="button"
            className="min-h-11 rounded-xl border border-input font-heading text-xs font-bold text-muted-foreground disabled:opacity-50"
            disabled={!students.filtersActive}
            onClick={students.clearFilters}
          >
            {t("sp.clearFilters")}
          </button>
        </div>
      )}
    </div>
  );
}

export function MobileStudentIdentityHeaderTools({
  locale,
  student,
}: {
  readonly locale: Locale;
  readonly student: StudentProfileContract;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  return (
    <div className="mt-3 flex flex-wrap gap-2 text-2xs">
      <span className="rounded-md bg-white/15 px-2 py-1">
        <Bidi>{normalizeSchoolLevelLabel(student.enrollment.classLabel)}</Bidi>
      </span>
      {student.massar && (
        <span className="rounded-md bg-white/15 px-2 py-1">
          <Bidi>{student.massar}</Bidi>
        </span>
      )}
      <span className="rounded-md bg-white/15 px-2 py-1">
        <Bidi>{student.internalReference}</Bidi>
      </span>
      <span className="rounded-md bg-white/15 px-2 py-1 font-bold">
        {t(lifecycleStatusKey[student.lifecycleStatus])}
      </span>
    </div>
  );
}

export function MobileStudentsScreen({
  locale,
  online,
  csrf,
  user,
  students,
  workspace,
  registrationRequested,
  onRegistrationRequestHandled,
}: {
  readonly locale: Locale;
  readonly online: boolean;
  readonly csrf: string | null;
  readonly user: AuthenticatedUserContract | null;
  readonly students: StudentSearchController;
  readonly workspace: WorkspaceController;
  readonly registrationRequested: boolean;
  readonly onRegistrationRequestHandled: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const [registerOpen, setRegisterOpen] = useState(false);
  const canAdminister = Boolean(user) && online && Boolean(csrf);

  useEffect(() => {
    if (!registrationRequested || !canAdminister) return;
    setRegisterOpen(true);
    onRegistrationRequestHandled();
  }, [canAdminister, onRegistrationRequestHandled, registrationRequested]);

  if (students.selected)
    return (
      <MobileStudentProfile
        key={students.selected.id}
        locale={locale}
        online={online}
        csrf={csrf}
        user={user}
        students={students}
        student={students.selected}
        workspace={workspace}
      />
    );

  return (
    <section data-testid="students-screen">
      <div className="p-3.5" id="student-results" aria-live="polite">
        {students.busy ? (
          <div className="overflow-hidden rounded-2xl bg-white shadow-e1">
            {[0, 1, 2].map((row) => (
              <div
                key={row}
                className="flex min-h-[76px] items-center gap-3 border-b border-border/40 px-4 py-3 last:border-b-0"
              >
                <span className="chams-skeleton size-10 shrink-0 rounded-xl" />
                <span className="flex flex-1 flex-col gap-2">
                  <span className="chams-skeleton block h-2.5 w-3/5" />
                  <span className="chams-skeleton block h-2 w-2/5" />
                </span>
              </div>
            ))}
          </div>
        ) : students.items.length ? (
          <div className="student-list overflow-hidden rounded-2xl bg-white shadow-e1">
            {students.items.map((student) => (
              <button
                key={student.id}
                type="button"
                className="student-row grid min-h-[76px] w-full grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-border/40 px-4 py-2.5 text-start last:border-b-0"
                dir={locale === "ar" ? "rtl" : "ltr"}
                onClick={() => void students.openStudent(student.id)}
              >
                <span className="student-row__avatar grid size-10 shrink-0 place-items-center rounded-xl bg-primary-soft font-heading text-sm font-bold text-primary-strong">
                  {initialOf(locale === "ar" ? student.nameAr : student.nameFr)}
                </span>
                <span className="flex min-w-0 flex-col items-start justify-center gap-0.5">
                  <strong className="w-full truncate font-heading text-[13.5px]">
                    {locale === "ar" ? student.nameAr : student.nameFr}
                  </strong>
                  <span className="flex w-full min-w-0 items-center gap-2">
                    <Bidi className="student-row__facts min-w-0 truncate text-2xs font-semibold text-muted-foreground">
                      {normalizeSchoolLevelLabel(student.currentClass)}
                      {student.sectionLabel ? `-${student.sectionLabel}` : ""}
                    </Bidi>
                    {student.lifecycleStatus !== "active" && (
                      <Badge
                        variant={student.lifecycleStatus === "graduated" ? "info" : "warning"}
                        className="h-5 shrink-0 px-2 text-[10px]"
                      >
                        {t(lifecycleStatusKey[student.lifecycleStatus])}
                      </Badge>
                    )}
                  </span>
                </span>
                <span className="flex items-center justify-self-end">
                  <ServiceIcons
                    className="justify-end"
                    locale={locale}
                    summaryAr={student.serviceSummaryAr}
                    summaryFr={student.serviceSummaryFr}
                  />
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl bg-white shadow-e1">
            <StudentsEmptyState
              locale={locale}
              filtered={students.filtersActive}
              action={
                students.filtersActive ? (
                  <button
                    type="button"
                    className="min-h-11 rounded-xl border border-border bg-white px-4 font-heading text-sm font-bold text-foreground"
                    onClick={students.clearFilters}
                  >
                    {t("sp.clearFilters")}
                  </button>
                ) : undefined
              }
            />
          </div>
        )}
      </div>

      <TabbarSpacer />

      {canAdminister && csrf && (
        <MobileStudentFormPopup
          locale={locale}
          csrf={csrf}
          opened={registerOpen}
          classOptions={classOptionsOf(workspace)}
          onClose={() => setRegisterOpen(false)}
          onSaved={students.studentSaved}
          onOpenStudent={(id) => void students.openStudent(id)}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

function SchedulePeriodRow({
  locale,
  entry,
  open,
  onToggle,
}: {
  readonly locale: Locale;
  readonly entry: ReturnType<typeof payablePeriods>[number];
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const state = periodState(entry);
  const fullAmount = entry.obligations
    .reduce((total, obligation) => total + BigInt(obligation.netMad), 0n)
    .toString();

  return (
    <div
      data-state={state}
      className="flex flex-col border-b border-border/40 last:border-b-0 data-[state=overdue]:bg-[#fdf9f9] data-[state=upcoming]:opacity-75"
    >
      <button
        type="button"
        aria-expanded={open}
        className="grid min-h-[58px] w-full grid-cols-[10px_minmax(0,1fr)_auto_20px] items-center gap-3 px-4 py-2.5 text-start"
        onClick={onToggle}
      >
        <span aria-hidden="true" className={`size-2.5 shrink-0 rounded-full ${stateDot[state]}`} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <strong className="truncate font-heading text-[13px]">
            {periodLabel(entry.period, locale)}
          </strong>
          <small className={`text-2xs ${stateText[state]}`}>{t(periodStateKey[state])}</small>
        </span>
        <span className="flex flex-col items-end gap-0.5 text-end">
          <strong className="font-heading text-sm">
            <Amount value={entry.remainingMad} locale={locale} />
          </strong>
          <small className="text-[9px] text-muted-foreground">
            {t("sp.settled")} <Amount value={entry.settledMad} locale={locale} /> · {t("sp.total")}{" "}
            <Amount value={fullAmount} locale={locale} />
          </small>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`size-4 text-muted-2 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="animate-fade-up border-t border-border/30 bg-surface-2 px-4 py-1.5">
          {entry.obligations.map((obligation) => (
            <div
              key={obligation.id}
              className="flex min-h-10 items-center justify-between gap-3 border-b border-border/30 py-2 text-xs last:border-b-0"
            >
              <span className="min-w-0 truncate text-muted-foreground">
                {locale === "ar" ? obligation.labelAr : obligation.labelFr}
              </span>
              <span className="flex shrink-0 flex-col items-end gap-0.5 text-end">
                <strong className="font-heading">
                  <Amount value={obligation.remainingMad} locale={locale} />
                </strong>
                <small className="text-[9px] text-muted-foreground">
                  {t("sp.settled")} <Amount value={obligation.settledMad} locale={locale} /> ·{" "}
                  {t("sp.total")} <Amount value={obligation.netMad} locale={locale} />
                </small>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MobileStudentProfile({
  locale,
  online,
  csrf,
  user,
  students,
  student,
  workspace,
}: {
  readonly locale: Locale;
  readonly online: boolean;
  readonly csrf: string | null;
  readonly user: AuthenticatedUserContract | null;
  readonly students: StudentSearchController;
  readonly student: StudentProfileContract;
  readonly workspace: WorkspaceController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const paymentText = paymentMessages(locale);
  const operationsText = operationsMessages(locale);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [advancePaymentOpen, setAdvancePaymentOpen] = useState(false);
  const [historicalPaymentOpen, setHistoricalPaymentOpen] = useState(false);
  const obligationYears = studentObligationYears(student);
  const historicalYears = obligationYears.historicalYears;
  const [historicalCalendarId, setHistoricalCalendarId] = useState(
    historicalYears[0]?.calendarSnapshotId ?? "__no-historical-year__",
  );
  const [openScheduleYear, setOpenScheduleYear] = useState<string | null>(
    obligationYears.showCurrentYear
      ? student.calendar.id
      : (historicalYears[0]?.calendarSnapshotId ?? null),
  );
  const [editOpen, setEditOpen] = useState(false);
  const [amountsOpen, setAmountsOpen] = useState(false);
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null);
  const toggleScheduleYear = (calendarSnapshotId: string) => {
    setOpenScheduleYear((current) => nextOpenScheduleYear(current, calendarSnapshotId));
    setExpandedPeriod(null);
  };
  const [tab, setTab] = useState<"schedule" | "history" | "details">("schedule");
  const [historyDocument, setHistoryDocument] = useState<LocalFinancialResultContract | null>(null);
  const [operationalPayment, setOperationalPayment] = useState<OperationalPaymentContract | null>(
    null,
  );
  const payment = usePayment({
    student,
    actorUserId: students.paymentActorId,
    trustedObservedAt: workspace.replica.synchronizedAt,
    captureReadiness: workspace.readiness,
    finalizedPayments: students.unsynchronizedHistory,
    calendarSnapshotId: student.calendar.id,
    onQueueChanged: () => void workspace.refreshLocalStatus(),
    onFinalized: () => {
      void students.reloadLocalHistory();
      showMobileFeedback("payment-recorded");
    },
  });
  const advancePayment = usePayment({
    student,
    actorUserId: students.paymentActorId,
    trustedObservedAt: workspace.replica.synchronizedAt,
    captureReadiness: workspace.readiness,
    finalizedPayments: students.unsynchronizedHistory,
    calendarSnapshotId: student.advanceYear?.calendarSnapshotId ?? "__no-advance-year__",
    onQueueChanged: () => void workspace.refreshLocalStatus(),
    onFinalized: () => {
      void students.reloadLocalHistory();
      showMobileFeedback("payment-recorded");
    },
  });
  const historicalPayment = usePayment({
    student,
    actorUserId: students.paymentActorId,
    trustedObservedAt: workspace.replica.synchronizedAt,
    captureReadiness: workspace.readiness,
    finalizedPayments: students.unsynchronizedHistory,
    calendarSnapshotId: historicalCalendarId,
    onQueueChanged: () => void workspace.refreshLocalStatus(),
    onFinalized: () => {
      void students.reloadLocalHistory();
      showMobileFeedback("payment-recorded");
    },
  });
  const canAdminister = Boolean(user) && online && Boolean(csrf);
  const canOperate = online && Boolean(csrf) && Boolean(user);
  const serviceAmounts = useStudentServiceAmounts({
    student,
    csrf: csrf ?? "",
    onSaved: (result) => {
      students.applyServiceAmounts(result);
      setAmountsOpen(false);
      showMobileFeedback("service-amounts-updated");
    },
  });
  const lifecycle = useStudentLifecycle({
    student,
    csrf: csrf ?? "",
    onSaved: (profile) => {
      students.studentSaved(profile);
      showMobileFeedback("lifecycle-updated");
    },
  });
  const periods = payablePeriods(payment.obligations);
  const advancePeriods = payablePeriods(advancePayment.obligations);
  const historicalYear = historicalYears.find(
    (year) => year.calendarSnapshotId === historicalCalendarId,
  );
  const advanceOutstanding = advancePayment.obligations
    .reduce((total, obligation) => total + BigInt(obligation.remainingMad), 0n)
    .toString();
  const overdue = periods.filter((entry) => entry.overdue && entry.remainingMad !== "0");
  const scheduleRows = (
    entries: typeof periods,
    remainingMad: string,
    paymentAction?: ReactNode,
  ) => (
    <>
      {entries.map((entry) => (
        <SchedulePeriodRow
          key={entry.period}
          locale={locale}
          entry={entry}
          open={expandedPeriod === entry.period}
          onToggle={() =>
            setExpandedPeriod((current) => (current === entry.period ? null : entry.period))
          }
        />
      ))}
      <div className="flex min-h-11 flex-wrap items-center gap-3 bg-surface-2 px-4 py-2.5">
        <span className="chams-eyebrow">{t("sp.remainingYear")}</span>
        <strong className="ms-auto font-heading text-sm">
          <Amount value={remainingMad} locale={locale} />
        </strong>
      </div>
      {paymentAction && <div className="bg-surface-2 px-4 pb-3">{paymentAction}</div>}
    </>
  );

  return (
    <section className="student-profile pt-3.5" data-testid="student-profile">
      <div className="student-financial-summary grid grid-cols-[1.3fr_1fr] gap-2.5 p-3.5">
        <div className="flex flex-col gap-1.5 rounded-2xl border-t-[3px] border-t-destructive bg-white p-4 shadow-e1">
          <span className="chams-eyebrow">{t("sp.outstanding")}</span>
          <strong className="font-heading text-2xl leading-none">
            <Amount value={student.obligationSummary.outstandingMad} locale={locale} />
          </strong>
        </div>
        <div className="flex flex-col gap-1.5 rounded-2xl bg-white p-4 shadow-e1">
          <span className="chams-eyebrow">{t("sp.overdueCount")}</span>
          <strong className="font-heading text-2xl leading-none text-warning">
            <Count value={student.obligationSummary.overdueCount} />
          </strong>
          <small className="text-2xs text-muted-foreground">
            {overdue.map((entry) => periodLabel(entry.period, locale)).join(" · ")}
          </small>
        </div>
      </div>

      <Block className="!my-0 px-3.5">
        <Segmented
          strong
          className="mobile-segmented"
          data-tab-count="3"
          data-active-index={tab === "schedule" ? "0" : tab === "history" ? "1" : "2"}
        >
          <SegmentedButton
            active={tab === "schedule"}
            className="min-h-11"
            onClick={() => setTab("schedule")}
          >
            {t("sp.feeScheduleTab")}
          </SegmentedButton>
          <SegmentedButton
            active={tab === "history"}
            className="min-h-11"
            onClick={() => setTab("history")}
          >
            {t("sp.paymentHistory")}
          </SegmentedButton>
          <SegmentedButton
            active={tab === "details"}
            className="min-h-11"
            onClick={() => setTab("details")}
          >
            {t("sp.details")}
          </SegmentedButton>
        </Segmented>
      </Block>

      {tab === "schedule" && (
        <div className="student-section compact-obligation-list m-3.5 animate-fade-up overflow-hidden rounded-2xl bg-white shadow-e1">
          {canOperate && csrf && student.lifecycleStatus !== "graduated" && (
            <div className="border-b border-border/50 bg-surface-2 p-3">
              <button
                type="button"
                aria-expanded={amountsOpen}
                className="flex min-h-11 w-full items-center justify-center rounded-xl border border-input bg-white px-3 font-heading text-xs font-bold text-primary-strong"
                onClick={() => setAmountsOpen(true)}
              >
                {t("sp.manageServiceAmounts")}
              </button>
            </div>
          )}
          {historicalYears.map((year) => {
            const yearObligations =
              year.calendarSnapshotId === historicalCalendarId
                ? historicalPayment.obligations
                : student.obligations.filter(
                    (obligation) => obligation.calendarSnapshotId === year.calendarSnapshotId,
                  );
            const yearPeriods = payablePeriods(yearObligations);
            const yearOutstanding = yearObligations
              .reduce((total, obligation) => total + BigInt(obligation.remainingMad), 0n)
              .toString();
            return (
              <div key={year.calendarSnapshotId}>
                <button
                  type="button"
                  aria-expanded={openScheduleYear === year.calendarSnapshotId}
                  className="flex min-h-14 w-full items-center gap-3 border-b border-border bg-white px-4 text-start"
                  onClick={() => {
                    setHistoricalCalendarId(year.calendarSnapshotId);
                    toggleScheduleYear(year.calendarSnapshotId);
                  }}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <strong className="font-heading text-xs">{t("sp.historicalYearGroup")}</strong>
                    <Bidi className="text-2xs text-muted-foreground">{year.academicYear}</Bidi>
                  </span>
                  <ChevronDown
                    className={`size-4 text-muted-2 transition-transform ${
                      openScheduleYear === year.calendarSnapshotId ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openScheduleYear === year.calendarSnapshotId &&
                  scheduleRows(
                    yearPeriods,
                    yearOutstanding,
                    students.paymentActorId ? (
                      <button
                        type="button"
                        className="min-h-11 w-full rounded-xl bg-primary px-3 font-heading text-2xs font-bold text-white disabled:bg-disabled"
                        disabled={historicalPayment.blocked}
                        onClick={() => setHistoricalPaymentOpen(true)}
                      >
                        {t("sp.takeHistoricalYearPayment")}
                      </button>
                    ) : undefined,
                  )}
              </div>
            );
          })}
          {obligationYears.showCurrentYear && (
            <>
              <button
                type="button"
                aria-expanded={openScheduleYear === student.calendar.id}
                className="flex min-h-14 w-full items-center gap-3 border-b border-border bg-white px-4 text-start"
                onClick={() => toggleScheduleYear(student.calendar.id)}
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <strong className="font-heading text-xs">{t("sp.currentYearGroup")}</strong>
                  <Bidi className="text-2xs text-muted-foreground">
                    {student.calendar.academicYear}
                  </Bidi>
                </span>
                <ChevronDown
                  className={`size-4 text-muted-2 transition-transform ${
                    openScheduleYear === student.calendar.id ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openScheduleYear === student.calendar.id &&
                scheduleRows(periods, student.obligationSummary.outstandingMad)}
            </>
          )}
          {obligationYears.showAdvanceYear && student.advanceYear && advancePeriods.length > 0 && (
            <>
              <button
                type="button"
                aria-expanded={openScheduleYear === student.advanceYear.calendarSnapshotId}
                className="flex min-h-14 w-full items-center gap-3 border-y border-border bg-white px-4 text-start"
                onClick={() => toggleScheduleYear(student.advanceYear!.calendarSnapshotId)}
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <strong className="font-heading text-xs">{t("sp.nextYearGroup")}</strong>
                  <Bidi className="text-2xs text-muted-foreground">
                    {student.advanceYear.academicYear}
                  </Bidi>
                </span>
                <ChevronDown
                  className={`size-4 text-muted-2 transition-transform ${
                    openScheduleYear === student.advanceYear!.calendarSnapshotId ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openScheduleYear === student.advanceYear.calendarSnapshotId &&
                scheduleRows(
                  advancePeriods,
                  advanceOutstanding,
                  students.paymentActorId ? (
                    <button
                      type="button"
                      className="min-h-11 w-full rounded-xl bg-primary px-3 font-heading text-2xs font-bold text-white disabled:bg-disabled"
                      disabled={advancePayment.blocked}
                      onClick={() => setAdvancePaymentOpen(true)}
                    >
                      {t("sp.takeNextYearPayment")}
                    </button>
                  ) : undefined,
                )}
            </>
          )}
          {obligationYears.allPaidAfterGraduation && (
            <p className="px-4 py-8 text-center text-xs text-muted-foreground">
              {t("sp.allObligationsPaid")}
            </p>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="student-section compact-payment-history m-3.5 animate-fade-up overflow-hidden rounded-2xl bg-white shadow-e1">
          {students.serverHistory.length || students.localHistory.length ? (
            <>
              {students.serverHistory.map((entry) => (
                <button
                  key={entry.payment.id}
                  type="button"
                  data-cancelled={entry.lifecycle === "cancelled"}
                  className="grid min-h-14 w-full grid-cols-[minmax(0,1fr)_76px_minmax(76px,auto)] items-center gap-3 border-b border-border/40 px-4 py-3 text-start last:border-b-0 data-[cancelled=true]:bg-destructive-soft/40"
                  onClick={() => setOperationalPayment(entry)}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <strong
                      data-cancelled={entry.lifecycle === "cancelled"}
                      className="truncate font-heading text-xs data-[cancelled=true]:text-muted-foreground data-[cancelled=true]:line-through"
                    >
                      <Bidi>{entry.payment.reference}</Bidi>
                    </strong>
                    <small className="text-2xs text-muted-foreground">
                      {paymentText[entry.payment.method.method]} ·{" "}
                      <DateValue value={entry.payment.receivedDate} />
                    </small>
                  </span>
                  <strong
                    data-cancelled={entry.lifecycle === "cancelled"}
                    className="text-end font-heading text-sm data-[cancelled=true]:text-muted-2 data-[cancelled=true]:line-through"
                  >
                    <Amount value={entry.payment.amountMad} locale={locale} />
                  </strong>
                  <small className="justify-self-end text-end text-2xs text-muted-foreground">
                    {operationsText[entry.lifecycle]}
                  </small>
                </button>
              ))}
              {students.unsynchronizedHistory.map((entry) => (
                <button
                  key={entry.payment.id}
                  type="button"
                  className="grid min-h-14 w-full grid-cols-[minmax(0,1fr)_76px_minmax(76px,auto)] items-center gap-3 border-b border-border/40 px-4 py-3 text-start last:border-b-0"
                  onClick={() => setHistoryDocument(entry)}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <strong className="truncate font-heading text-xs">
                      <Bidi>{entry.payment.reference}</Bidi>
                    </strong>
                    <small className="text-2xs text-muted-foreground">
                      {paymentText[entry.payment.method.method]} ·{" "}
                      <DateValue value={entry.payment.receivedDate} />
                    </small>
                  </span>
                  <strong className="text-end font-heading text-sm">
                    <Amount value={entry.payment.amountMad} locale={locale} />
                  </strong>
                  <small className="justify-self-end text-end text-2xs text-muted-foreground">
                    {paymentText[entry.queueState]}
                  </small>
                </button>
              ))}
            </>
          ) : (
            <p className="student-section__empty px-4 py-8 text-center text-sm text-muted-foreground">
              {t("students.noPayments")}
            </p>
          )}
        </div>
      )}

      {tab === "details" && (
        <>
          <div className="student-section student-details m-3.5 animate-fade-up overflow-hidden rounded-2xl bg-white shadow-e1">
            {canAdminister && (
              <div className="border-b border-border/50 bg-surface-2 p-3">
                <button
                  type="button"
                  aria-expanded={editOpen}
                  className="flex min-h-11 w-full items-center justify-center rounded-xl border border-input bg-white px-3 font-heading text-xs font-bold text-primary-strong"
                  onClick={() => setEditOpen(true)}
                >
                  {t("sp.editStudent")}
                </button>
              </div>
            )}
            <div className="flex flex-col gap-3 p-4 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">{t("sp.guardian")}</span>
                <strong className="font-heading">{student.primaryGuardian.name}</strong>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">{t("sp.birthDate")}</span>
                <strong>
                  <DateValue value={student.birthDate} />
                </strong>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">{t("sp.enrolledOn")}</span>
                <strong>
                  <DateValue value={student.enrollment.enrolledAt.slice(0, 10)} />
                </strong>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">{t("students.enrollment")}</span>
                <strong>
                  <Bidi>{student.enrollment.academicYear}</Bidi>
                </strong>
              </div>
            </div>
          </div>
          <MobileStudentLifecycle
            locale={locale}
            student={student}
            controller={lifecycle}
            editable={canAdminister}
          />
        </>
      )}

      <TabbarSpacer />

      <MobileSurface
        opened={amountsOpen && canOperate && Boolean(csrf)}
        title={t("sp.manageServiceAmounts")}
        subtitle={locale === "ar" ? student.nameAr : student.nameFr}
        closeLabel={t("common.close")}
        testId="student-service-amounts-surface"
        onClose={() => setAmountsOpen(false)}
      >
        <form
          className="flex flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void serviceAmounts.submit();
          }}
        >
          <Block className="flex flex-1 flex-col gap-3">
            <p className="px-1 text-2xs leading-relaxed text-muted-foreground">
              {t("sp.manageServiceAmountsHelp")}
            </p>
            {serviceAmounts.academicYears.length > 1 && (
              <label className="flex flex-col gap-1 px-1 text-2xs font-bold">
                {t("students.academicYear")}
                <FilterSelect
                  value={serviceAmounts.calendarSnapshotId}
                  className="h-11 rounded-xl bg-white"
                  options={serviceAmounts.academicYears.map((year) => ({
                    value: year.calendarSnapshotId,
                    label: year.academicYear,
                  }))}
                  onChange={serviceAmounts.setCalendarSnapshotId}
                />
              </label>
            )}
            <div className="grid grid-cols-2 gap-2">
              {serviceAmountFields.map(({ family, label }) => (
                <label
                  key={family}
                  className="flex min-w-0 flex-col gap-1 rounded-xl border border-border bg-white p-2.5 text-2xs font-bold"
                >
                  <span className="truncate">{t(label)}</span>
                  <input
                    className="chams-figure h-11 min-w-0 rounded-lg border border-input bg-white px-2 text-end text-sm outline-none disabled:bg-sunken disabled:text-muted-foreground"
                    type="number"
                    min="0"
                    step="1"
                    dir="ltr"
                    disabled={!serviceAmounts.available[family]}
                    placeholder={
                      serviceAmounts.enabled[family] ? undefined : t("sp.serviceNotEnabled")
                    }
                    value={serviceAmounts.values[family]}
                    onChange={(event) => serviceAmounts.setAmount(family, event.target.value)}
                  />
                </label>
              ))}
            </div>
            <label className="flex flex-col gap-1 px-0.5 text-2xs font-bold">
              {t("sp.reason")}
              <textarea
                className="min-h-20 rounded-xl border border-input bg-white px-3 py-2.5 text-xs font-normal outline-none"
                required
                minLength={3}
                maxLength={500}
                value={serviceAmounts.reason}
                onChange={(event) => serviceAmounts.setReason(event.target.value)}
              />
            </label>
            <span className="px-1 text-2xs text-muted-foreground">
              {t("sp.serviceAmountsPaidNote")}
            </span>
            {serviceAmounts.failed && (
              <span className="px-1 text-2xs text-destructive">{operationsText.actionFailed}</span>
            )}
          </Block>
          <div className="mobile-drawer-footer">
            <button
              type="submit"
              className="min-h-12 flex-1 rounded-xl bg-primary px-4 font-heading text-xs font-bold text-white disabled:bg-disabled"
              disabled={serviceAmounts.submitDisabled}
            >
              {t("sp.saveServiceAmounts")}
            </button>
          </div>
        </form>
      </MobileSurface>

      {students.paymentActorId && !paymentOpen && !advancePaymentOpen && !historicalPaymentOpen && (
        <button
          type="button"
          aria-label={t("sp.takePayment")}
          className="student-payment-action mobile-primary-action chams-focusable fixed bottom-24 end-[18px] z-[42] grid size-[62px] place-items-center rounded-2xl bg-primary text-white shadow-[0_10px_22px_-6px_rgb(42_138_148/0.7)] disabled:cursor-not-allowed disabled:bg-teal-200"
          disabled={payment.blocked}
          onClick={() => setPaymentOpen(true)}
        >
          <Plus aria-hidden="true" className="size-6" strokeWidth={2} />
        </button>
      )}

      {students.paymentActorId && (
        <MobilePaymentPopup
          locale={locale}
          online={online}
          csrf={csrf ?? ""}
          student={student}
          payment={payment}
          synchronize={async () => {
            await workspace.sync();
          }}
          opened={paymentOpen}
          onClose={() => setPaymentOpen(false)}
        />
      )}
      {students.paymentActorId && student.advanceYear && (
        <MobilePaymentPopup
          locale={locale}
          online={online}
          csrf={csrf ?? ""}
          student={student}
          payment={advancePayment}
          yearContext={{ role: "next", academicYear: student.advanceYear.academicYear }}
          synchronize={async () => {
            await workspace.sync();
          }}
          opened={advancePaymentOpen}
          onClose={() => setAdvancePaymentOpen(false)}
        />
      )}
      {students.paymentActorId && historicalYear && (
        <MobilePaymentPopup
          locale={locale}
          online={online}
          csrf={csrf ?? ""}
          student={student}
          payment={historicalPayment}
          yearContext={{ role: "historical", academicYear: historicalYear.academicYear }}
          synchronize={async () => {
            await workspace.sync();
          }}
          opened={historicalPaymentOpen}
          onClose={() => setHistoricalPaymentOpen(false)}
        />
      )}

      <MobileSurface
        opened={historyDocument !== null}
        title={historyDocument?.payment.reference ?? paymentText.history}
        closeLabel={t("common.close")}
        testId="payment-history-surface"
        onClose={() => setHistoryDocument(null)}
      >
        {historyDocument && (
          <div className="payment-history-document pb-8">
            <Block className="!my-3 text-sm text-muted-foreground">
              {paymentText[historyDocument.queueState]}
            </Block>
            <Block strong inset>
              <MobileA4DocumentPreview locale={locale}>
                <FinancialDocument locale={locale} snapshot={historyDocument.document} />
              </MobileA4DocumentPreview>
            </Block>
          </div>
        )}
      </MobileSurface>

      {online && csrf && (
        <MobileOperationalPaymentPopup
          locale={locale}
          payment={operationalPayment}
          csrf={csrf}
          onClose={() => setOperationalPayment(null)}
          onChanged={() => {
            void students.refreshSelected();
            showMobileFeedback("payment-cancelled");
          }}
        />
      )}

      {canAdminister && csrf && (
        <MobileStudentFormPopup
          locale={locale}
          csrf={csrf}
          opened={editOpen}
          student={student}
          classOptions={classOptionsOf(workspace)}
          onClose={() => setEditOpen(false)}
          onSaved={students.studentSaved}
        />
      )}
    </section>
  );
}
